/**
 * @flow
 * @providesModule CasualLink
 */

import type {PointType, TransitionInfoType} from "./stories-shared";

export type RectCoordinatesType = {
    x: number;
    y: number;
    x1: number;
    y1: number;
}

export type DataCallbackType<T> = (d: T) => PointType;

let abs = Math.abs;

const between = function (val: number, sp: number, lp: number): boolean {
    return val >= sp && val <= lp;
};

export class CasualLink {
    _source: DataCallbackType<TransitionInfoType>;
    _target: DataCallbackType<TransitionInfoType>;
    _useVersion = 0;
    _useAbsoluteCoord = false;

    taskHeight = 0;
    taskWidth = 0;
    taskHalfWidth = 0;
    taskHalfHeight = 0;
    _margin = 30;

    constructor(source:DataCallbackType<TransitionInfoType>, target:DataCallbackType<TransitionInfoType>, useVersion:number, useAbsoluteCoord:boolean) {
        this._source = source;
        this._target = target;
        this._useVersion = useVersion;
        this._useAbsoluteCoord = useAbsoluteCoord;
    }

    set margin(value:number) {
        this._margin = value;
    }
    set useAbsoluteCoord(value:boolean) {
        this._useAbsoluteCoord = value;
    }
    set useVersion(value:number) {
        this._useVersion = value;
    }
    get target():DataCallbackType<TransitionInfoType> {
        return this._target;
    }

    set target(value:DataCallbackType<TransitionInfoType>) {
        this._target = value;
    }
    get source():DataCallbackType<TransitionInfoType> {
        return this._source;
    }

    set source(value:DataCallbackType<TransitionInfoType>) {
        this._source = value;
    }

    path(d: TransitionInfoType, justCoordinates?: boolean): string | RectCoordinatesType {
        const p0 = this._source(d);
        const p3 = this._target(d);

        switch (this._useVersion) {
            default:
                let p0x = p0.x + this.taskHalfWidth;
                let p0y = p0.y + this.taskHeight;
                let p3x = p3.x + this.taskHalfWidth;
                let p3y = p3.y - 2;
                let cx = (p0x - p3x) / 2;
                let cy = (p0y - p3y) / 2;

                return justCoordinates
                    ? {x: p0x, y: p0y, x1: p3x, y1: p3y}
                    : p0.x == p3.x && p0.y == p3.y
                    ? `M${p0x},${p0y}l0,1`
                    : `M${p0x},${p0y}Q${p0x},${p0y + this._margin} ${p0x - cx},${p0y - cy}T${p3x},${p3y}`;

            case 1:
                const $fo = p0;
                const $to = p3;
                const dx = $fo.x - $to.x;
                const dy = $fo.y - $to.y;
                const angle = Math.atan2(dy, dx) * 180 / Math.PI;
                const aangle = abs(angle);
                const distance = Math.sqrt(dx * dx + dy * dy);
                const p = [];

                let nfox: number = 0;
                let nfoy: number = 0;
                let ntox: number = 0;
                let ntoy: number = 0;
                let ofsp: number | string | Array<number> = 0;

                switch (true) {
                    case between(aangle, 0, 45): // left quoter
                        switch (true) {
                            case between(angle, 10, 45):
                                nfox = $fo.x;
                                nfoy = $fo.y + this.taskHalfHeight;
                                ntox = $to.x + this.taskWidth + 2;
                                ntoy = $to.y + this.taskHalfHeight;
                                ofsp = [nfox - this._margin, nfoy];
                                break;
                            case angle < 0 && dx < this.taskWidth && abs(dy) < this.taskHeight + this._margin:
                                nfox = $fo.x;
                                nfoy = $fo.y + this.taskHalfHeight;
                                ntox = $to.x + this.taskHalfWidth;
                                ntoy = $to.y - 2;
                                ofsp = `C${nfox - this._margin},${nfoy} ${ntox},${ntoy - this._margin} ${ntox},${ntoy}`;
                                break;
                            case between(aangle, 0, 10):
                                nfox = $fo.x;
                                nfoy = $fo.y + this.taskHalfHeight;
                                ntox = $to.x + this.taskWidth + 2;
                                ntoy = $to.y + this.taskHalfHeight;
                                break;
                            default:
                                nfox = $fo.x + this.taskHalfWidth;
                                nfoy = $fo.y + this.taskHeight;
                                ntox = $to.x + this.taskHalfWidth;
                                ntoy = $to.y;
                                ofsp = [nfox, nfoy + this._margin];
                                break;
                        }
                        break;
                    case angle < 0 && between(aangle, 45, 135): // bottom
                        nfox = $fo.x + this.taskHalfWidth;
                        nfoy = $fo.y + this.taskHeight;
                        ntox = $to.x + this.taskHalfWidth;
                        ntoy = $to.y - 2;
                        ofsp = [nfox, nfoy + this._margin];
                        break;
                    case between(aangle, 170, 180): // right
                        nfox = $fo.x + this.taskWidth;
                        nfoy = $fo.y + this.taskHalfHeight;
                        ntox = $to.x - 2;
                        ntoy = $to.y + this.taskHalfHeight;
                        ofsp = [nfox, nfoy + this._margin];
                        break;
                    case angle > 0 && between(angle, 45, 170): // top
                        switch (true) {
                            case $fo.x + this.taskWidth > $to.x:
                                nfox = $fo.x + this.taskWidth;
                                nfoy = $fo.y + this.taskHalfHeight;
                                ntox = $to.x + this.taskWidth;
                                ntoy = $to.y + this.taskHalfHeight;
                                ofsp = `C${nfox + this._margin},${nfoy} ${ntox + this._margin},${ntoy} ${ntox},${ntoy}`;
                                break;
                            default:
                                nfox = $fo.x + this.taskWidth;
                                nfoy = $fo.y + this.taskHalfHeight;
                                ntox = $to.x - 2;
                                ntoy = $to.y + this.taskHalfHeight;
                                ofsp = [nfox + this._margin, nfoy];
                                break;
                        }
                        break;
                    case angle < 0 && between(aangle, 135, 170):
                        switch (true) {
                            case $to.x < $fo.x + this.taskWidth:
                                nfox = $fo.x + this.taskHalfWidth;
                                nfoy = $fo.y + this.taskHeight;
                                ntox = $to.x + this.taskHalfWidth;
                                ntoy = $to.y;
                                ofsp = [nfox, nfoy + this._margin];
                                break;
                            case distance < 300:
                                nfox = $fo.x + this.taskWidth;
                                nfoy = $fo.y + this.taskHalfHeight;
                                ntox = $to.x;
                                ntoy = $to.y + this.taskHalfHeight;
                                ofsp = [nfox + this._margin, nfoy];
                                break;
                            default:
                                nfox = $fo.x + this.taskHalfWidth;
                                nfoy = $fo.y + this.taskHeight;
                                ntox = $to.x + this.taskHalfWidth;
                                ntoy = $to.y;
                                ofsp = [nfox, nfoy + this._margin];
                                break;
                        }
                        break;
                }

                p.push(`M${nfox},${nfoy}`);
                // stright left, right, top and bottom
                if (distance < 100 || between(aangle, 0, 10) || angle < 0 && between(aangle, 80, 100) || between(aangle, 170, 180)) {
                    p.push(`L${ntox},${ntoy}`);
                } else {
                    let cx = (nfox - ntox) / 2;
                    let cy = (nfoy - ntoy) / 2;

                    if (ofsp instanceof Array) {
                        p.push(`Q${ofsp.toString()}`);
                        p.push(` ${nfox - cx},${nfoy - cy}`);
                        p.push(`T${ntox},${ntoy}`);
                    } else {
                        p.push(ofsp);
                    }
                }

                // console.warn(distance, angle);

                return justCoordinates
                    ? {x: nfox, y: nfoy, x1: ntox, y1: ntoy}
                    : p.join('');
        }

    };

    useBoxDim(width: number, height: number):void {
        this.taskWidth = width;
        this.taskHeight = height;
        this.taskHalfWidth = width / 2;
        this.taskHalfHeight = height / 2;
    };
}



// WEBPACK FOOTER //
// ./src/lib/link.js